import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent, BOOKING_SCREENING_URL, BOOKING_THERAPY_URL } from '../lib/tracking';
import { optimizeImage } from '../lib/image';
import ProcessSection from '../components/ProcessSection';
import SatisfactionGuaranteeModal from '../components/SatisfactionGuaranteeModal';

export default function TherapyAndHypnosis() {
  const [isGuaranteeOpen, setIsGuaranteeOpen] = useState(false);

  return (
    <div className="bg-[#ECEEF1]">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-3">
              Individuelle forløb
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#5A232E] tracking-tight leading-[1.12]">
              Psykoterapi & Hypnoterapi på Amager
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#6c7480] leading-relaxed">
              Få hjælp til at bryde negative mønstre, forløse traumer og genfinde balancen i hverdagen. Hos Psykoterapeut MPF Kim Palasz kombineres evidensbaseret samtaleterapi, NLP og hypnoterapi i et trygt og fortroligt rum.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={BOOKING_SCREENING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('cta_booking_click', { location: 'therapy_hypnosis_hero' })}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
              >
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                <span>Book en gratis afklaringssamtale</span>
              </a>
              <button
                type="button"
                onClick={() => setIsGuaranteeOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl bg-[#ECEEF1] text-[#5A232E] font-medium text-sm border border-[#D9DDE2] hover:bg-[#D9DDE2] transition-colors"
              >
                <i className="fa-solid fa-shield-check text-[#5A232E]" aria-hidden="true" />
                <span>100% tilfredshedsgaranti</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 1. Hvad kan du få hjælp til? */}
      <section className="py-16 sm:py-20 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Udfordringer
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Hvad kan du få hjælp til?
            </h2>
            <p className="mt-3 text-sm sm:text-base text-[#6c7480]">
              Terapien tager altid udgangspunkt i det, du kæmper med her og nu:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Stress & udbrændthed', link: '/stress/', desc: 'Beroligelse af nervesystemet, grænsesætning og overskud.' },
              { title: 'Angst & panik', link: '/angst/', desc: 'Forstå kroppens signaler og lær at regulere frygtresponsen.' },
              { title: 'Traumer (TBT)', link: '/traumer/', desc: 'Skånsom forløsning af chok, ulykker, overgreb og svigt.' },
              { title: 'Lavt selvværd', link: '/lavt-selvvaerd/', desc: 'Dæmp selvkritikken og find indre ro og styrke.' },
              { title: 'Sorg & livskriser', link: '/sorg-og-krise/', desc: 'Støtte ved tab, skilsmisse, sygdom og overvældelse.' },
              { title: 'Vaner: rygestop & sukkertrang', link: '/hypnose-vaner/', desc: 'Målrettet hypnoterapi til nikotin- og sukkertrang.' },
              { title: 'Depression & modløshed', link: '/depression/', desc: 'Genfind retning, mening og kontakt til egne ressourcer.' },
              { title: 'Relationer & grænser', link: null, desc: 'Skab ligeværdige og trygge relationer uden at miste dig selv.' },
            ].map((item, i) => (
              item.link ? (
                <Link
                  key={i}
                  to={item.link}
                  className="bg-white p-6 rounded-2xl border border-[#D9DDE2] hover:border-[#5A232E]/60 shadow-2xs hover:shadow-md transition-all group flex flex-col justify-between"
                >
                  <div>
                    <h3 className="font-display text-lg text-[#5A232E] group-hover:text-[#5A232E] transition-colors mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs text-[#6c7480] leading-relaxed mb-4">
                      {item.desc}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-[#5A232E] flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    Læs om forløb →
                  </span>
                </Link>
              ) : (
                <div
                  key={i}
                  className="bg-white p-6 rounded-2xl border border-[#D9DDE2] shadow-2xs flex flex-col justify-between"
                >
                  <div>
                    <h3 className="font-display text-lg text-[#5A232E] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs text-[#6c7480] leading-relaxed mb-4">
                      {item.desc}
                    </p>
                  </div>
                  <span className="text-xs font-medium text-[#6c7480]">
                    Indgår i det terapeutiske forløb
                  </span>
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* 2. Dybdegående præsentation af Psykoterapi & Hypnose */}
      <section className="py-20 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {/* Psykoterapi Block */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-5">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E]">
                Samtaleterapi · NLP · TBT
              </span>
              <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
                Psykoterapi hos Kim Palasz
              </h2>
              <div className="text-sm sm:text-base text-[#11161d] space-y-3 leading-relaxed">
                <p>
                  Terapi er en professionel samtale i et trygt og fortroligt rum. Mit udgangspunkt er, at du er mere end dine problemer. Terapien foregår i dit eget tempo – med respekt, varme og høj faglighed.
                </p>
                <p>
                  Jeg arbejder med <strong>NLP (Neuro Lingvistisk Programmering)</strong>, en psykologisk metode der hjælper dig med at aktivere dine ubevidste ressourcer og skabe balance mellem dit bevidste og ubevidste sind.
                </p>
                <p>
                  Traumer og fastlåste oplevelser forløses skånsomt med <strong>Transbiologisk Traume Terapi (TBT)</strong>, familieopstilling og heling af det indre barn, så nervesystemet endelig kan slippe sit alarmberedskab.
                </p>
              </div>

              <div className="pt-2 flex items-center gap-4">
                <a
                  href={BOOKING_THERAPY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 rounded-xl bg-[#5A232E] text-white text-sm font-semibold hover:bg-[#431722] transition-colors"
                >
                  Book terapisession (60 min.)
                </a>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-[#ECEEF1]">
                <img
                  src={optimizeImage('kim-outdoor-park-4.webp', 800)}
                  alt="Kim Palasz i rolige naturomgivelser"
                  className="w-full h-auto object-cover max-h-[440px]"
                />
              </div>
            </div>
          </div>

          {/* Hypnoterapi Block - Justeret fokus */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-[#ECEEF1]">
                <img
                  src={optimizeImage(
                    'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/2222.jpg',
                    800
                  )}
                  alt="Kim Palasz i terapisession og samtale med klient"
                  className="w-full h-auto object-cover max-h-[440px]"
                />
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2 space-y-5">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E]">
                Faglig Hypnoterapi & Terapeutisk Samarbejde
              </span>
              <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
                Hypnoterapi – hvad er det, og hvordan virker det?
              </h2>
              <div className="text-sm sm:text-base text-[#11161d] space-y-3 leading-relaxed">
                <p>
                  Mange har hørt om hypnoterapi, men er i tvivl om, hvad det egentlig er. I virkeligheden er hypnoterapi en tryg, videnskabeligt funderet terapeutisk metode, hvor du guides ind i en behagelig tilstand af dyb afslapning og fokuseret indre opmærksomhed.
                </p>
                <p>
                  Du er ved fuld bevidsthed hele tiden og bevarer kontrollen. Hypnoterapi bruges bredt i psykoterapien, og Kim anvender ofte hypnose som en integreret del af arbejdet med <strong>traumer</strong>, <strong>automatiske reaktioner</strong>, <strong>følelsesmæssige mønstre</strong> og <strong>indre uro</strong>, fordi metoden arbejder direkte med de underbevidste lag, hvor kroppen og sindet gemmer uforløste oplevelser.
                </p>
                <div className="p-4 rounded-xl bg-[#ECEEF1] border border-[#D9DDE2] text-xs sm:text-sm text-[#11161d] space-y-2">
                  <p className="font-semibold text-[#5A232E]">Hvad kan hypnoterapi anvendes til?</p>
                  <ul className="list-disc list-inside space-y-1 text-[#6c7480]">
                    <li>Bearbejdning af traumer, chok og fastlåste oplevelser</li>
                    <li>Automatiske følelsesmæssige reaktioner og indre uro</li>
                    <li>Styrkelse af selvværd og afvæbning af indre selvkritik</li>
                    <li>Målrettede vaneforløb, såsom rygestop, vaping, snus og sukkertrang</li>
                  </ul>
                </div>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <a
                  href={BOOKING_THERAPY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 rounded-xl bg-[#5A232E] text-white text-sm font-semibold hover:bg-[#431722] transition-colors"
                >
                  Book hypnoterapisession (60 min.)
                </a>
                <a
                  href={BOOKING_SCREENING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-3 rounded-xl bg-[#ECEEF1] text-[#5A232E] text-sm font-medium border border-[#D9DDE2] hover:bg-[#D9DDE2] transition-colors"
                >
                  Gratis afklaring (20 min.)
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2b. SELVSTÆNDIGE BEHANDLINGSTILBUD FOR MÅLRETTET HYPNOSE */}
      <section className="py-20 sm:py-24 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Målrettede Forløb
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Specifikke hypnosebehandlinger
            </h2>
            <p className="mt-3 text-sm sm:text-base text-[#6c7480] leading-relaxed">
              Ud over det brede terapeutiske arbejde tilbydes målrettede forløb mod automatiske vanemønstre og afhængighed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Behandling 1: Rygestop, vaping og snus */}
            <div className="bg-white rounded-3xl p-8 sm:p-10 border border-[#D9DDE2] shadow-xs flex flex-col justify-between">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5A232E]/10 text-[#5A232E] text-xs font-semibold uppercase tracking-wider">
                  <i className="fa-solid fa-ban-smoking" aria-hidden="true" />
                  Nikotinstop
                </div>
                <h3 className="font-display text-2xl sm:text-3xl text-[#5A232E]">
                  Hypnose til rygestop, vaping og snus
                </h3>
                <div className="text-sm sm:text-base text-[#11161d] space-y-3 leading-relaxed">
                  <p>
                    Vil du gerne stoppe med nikotin, men oplever, at trangen eller vanen bliver ved med at vende tilbage?
                  </p>
                  <p>
                    Med hypnose arbejder vi med de automatiske mønstre og vaner, der kan være forbundet med rygning, vaping, snus og andre nikotinprodukter.
                  </p>
                  <p>
                    Målet er at gøre det lettere at slippe nikotinen, så du ikke hele tiden skal bruge viljestyrke på at holde fast i din beslutning.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-[#D9DDE2] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-xs text-[#6c7480]">
                  <span className="font-bold text-[#5A232E] text-base block">3.000 kr.</span>
                  2-3 sessioner forløb (inkl. moms)
                </div>
                <a
                  href={BOOKING_THERAPY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-xs transition-colors"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Book rygestopforløb</span>
                </a>
              </div>
            </div>

            {/* Behandling 2: Sukkertrang og usunde madvaner */}
            <div className="bg-white rounded-3xl p-8 sm:p-10 border border-[#D9DDE2] shadow-xs flex flex-col justify-between">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5A232E]/10 text-[#5A232E] text-xs font-semibold uppercase tracking-wider">
                  <i className="fa-solid fa-cookie-bite" aria-hidden="true" />
                  Madvaner & Trang
                </div>
                <h3 className="font-display text-2xl sm:text-3xl text-[#5A232E]">
                  Hypnose mod sukkertrang og usunde madvaner
                </h3>
                <div className="text-sm sm:text-base text-[#11161d] space-y-3 leading-relaxed">
                  <p>
                    Sukkertrang og usunde madvaner handler ikke altid om sult eller manglende disciplin. For mange bliver bestemte måder at spise på automatiske vaner.
                  </p>
                  <p>
                    Med hypnose arbejder vi med de mønstre og den trang, der kan få dig til at række ud efter sukker, snacks eller usund mad – selvom du egentlig ønsker noget andet.
                  </p>
                  <p>
                    Målet er, at trangen fylder mindre, og at du får større frihed til selv at vælge.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-[#D9DDE2] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-xs text-[#6c7480]">
                  <span className="font-bold text-[#5A232E] text-base block">3.000 kr.</span>
                  2-3 sessioner forløb (inkl. moms)
                </div>
                <a
                  href={BOOKING_THERAPY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-xs transition-colors"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Book forløb mod sukkertrang</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Process */}
      <ProcessSection />

      {/* Bottom CTA */}
      <section className="py-20 bg-[#5A232E] text-white text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="font-display text-3xl sm:text-4xl text-white">
            Er du i tvivl om, hvad du skal vælge?
          </h2>
          <p className="text-sm sm:text-base text-white/80 max-w-xl mx-auto">
            Book en 20 minutters gratis afklaringssamtale. Her taler vi uforpligtende om din situation og finder den bedste vej frem for dig.
          </p>
          <a
            href={BOOKING_SCREENING_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('cta_booking_click', { location: 'therapy_hypnosis_bottom' })}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white hover:bg-[#ECEEF1] text-[#5A232E] font-semibold text-base shadow-xl transition-all"
          >
            <i className="fa-solid fa-calendar-check" aria-hidden="true" />
            <span>Book gratis afklaringssamtale</span>
          </a>
        </div>
      </section>

      <SatisfactionGuaranteeModal
        isOpen={isGuaranteeOpen}
        onClose={() => setIsGuaranteeOpen(false)}
      />
    </div>
  );
}
